import React, { Suspense, useCallback, useEffect, useState } from "react";
import "./RewardTypeList.css";
import Button from "../../../../../components/reusable-button/Button";
import Loader from "../../../../../components/reusable-loader/Loader";
import { apiClient } from "../../../../../utils/apiClient";
import { useDispatch } from "react-redux";
import { setSnackbarDetails } from "../../../../../redux/AuthReducer/AuthSlice";
import { reactIcons } from "../../../../../icons/reactIcons";
import {
  formatDateOnly,
  formatTime,
} from "../../../../../utils/utlilityFunctions";
import CreateRewardType from "../create-reward-type/CreateRewardType";
import EditRewardType from "../edit-reward-type/EditRewardType";
import DeleteRewardType from "../delete-reward-type/DeleteRewardType";

const Table = React.lazy(
  () => import("../../../../../components/reusable-table/Table"),
);

export interface rewardType {
  id: number;
  name: string;
  createdAt: string; // ISO string
  updatedAt: string; // ISO string
}

export interface rewardTypeListResponse {
  success: boolean;
  data: rewardType[];
}

const columns = [
  { header: "Reward Name", accessor: "name" },
  { header: "Created At", accessor: "createdAt" },
];

const RewardTypeList = () => {
  const dispatcher = useDispatch();
  const [rewardTypeList, setRewardTypeList] = useState<rewardType[]>([]);

  //using the following state to store the status of createReward Type Modal
  const [createRewardTypeModalStatus, setCreateRewardTypeModalStatus] =
    useState<boolean>(false);

  //using the following state to store the details of rewardType based on id
  const [rewardTypeById, setRewardTypeById] = useState<rewardType | null>(null);

  //using the following state to store the  status of edit RewardType modal
  const [editRewardTypeModalStatus, setEditRewardTypeModalStatus] =
    useState<boolean>(false);

  //using the following state to store the status of delete Modal
  const [deleteModalStatus, setDeleteModalStatus] = useState<boolean>(false);

  //using the following state to render the loader
  const [loader, setLoader] = useState<boolean>(false);

  const actions = [
    {
      label: <reactIcons.delete />,
      onClick: (row: any) => handleDelete(row),
    },
    {
      label: <reactIcons.edit />,
      onClick: (row: any) => handleEdit(row),
    },
  ];

  const getRewardTypeList = useCallback(async (signal: AbortSignal) => {
    try {
      let res = await apiClient?.get<rewardTypeListResponse>(
        `/reward-type/list`,
        { signal: signal },
      );
      let data = res?.data?.data.map((item: rewardType) => {
        return {
          ...item,
          createdAt: `${formatDateOnly(new Date(item?.createdAt))} | ${formatTime(new Date(item?.createdAt))}`,
        };
      });
      setRewardTypeList(data);
    } catch (error: any) {
      dispatcher(
        setSnackbarDetails({
          type: "error",
          message:
            error?.response?.data?.data?.message ??
            "error in fetching the Reward Type list!",
        }),
      );
    }
  }, []);

  useEffect(() => {
    async function fetchRewardTypeList() {
      setLoader(true);
      try {
        let controller = new AbortController();
        await getRewardTypeList(controller?.signal);
      } catch (error) {
        console.log(error);
      } finally {
        setLoader(false);
      }
    }
    fetchRewardTypeList();
  }, [getRewardTypeList]);

  const handleDelete = (row: rewardType) => {
    setRewardTypeById(row);
    setDeleteModalStatus(true);
  };

  const handleEdit = (row: rewardType) => {
    setRewardTypeById(row);
    setEditRewardTypeModalStatus(true);
  };

  return (
    <div className="rewardType">
      <div className="rewardType-header">
        <h2>Department</h2>
        <div>
          <Button
            text="Create Reward Type"
            onClick={() => setCreateRewardTypeModalStatus(true)}
            type="button"
          />
        </div>
      </div>
      {loader ? (
        <Loader />
      ) : (
        <div className="department-table-wrapper">
          <Suspense>
            <Table actions={actions} columns={columns} data={rewardTypeList} />
          </Suspense>
        </div>
      )}
      {createRewardTypeModalStatus && (
        <CreateRewardType
          isOpen={createRewardTypeModalStatus}
          refreshList={getRewardTypeList}
          onClose={() => setCreateRewardTypeModalStatus(false)}
        />
      )}
      {editRewardTypeModalStatus && (
        <EditRewardType
          isOpen={editRewardTypeModalStatus}
          details={rewardTypeById}
          refreshList={getRewardTypeList}
          onClose={() => setEditRewardTypeModalStatus(false)}
        />
      )}
      {deleteModalStatus && (
        <DeleteRewardType
          isOpen={deleteModalStatus}
          details={rewardTypeById}
          refreshList={getRewardTypeList}
          onClose={() => setDeleteModalStatus(false)}
        />
      )}
    </div>
  );
};

export default RewardTypeList;
